import React from 'react'

import { Body, Spacer, TitleLarge, TitleSmall } from 'ethos-design-system'
import PropTypes from 'prop-types'

import ColoredPaddedContainer from '../global/ColoredPaddedContainer'
import Markdown, { MARKDOWN_NODE_RESTRICTION_SETS } from '../global/Markdown'
import styles from './Story.module.scss'

export const Story = ({ moduleData }: any) => {
  const {
    heading,
    subHeading,
    displaySubHeading,
    columnOne,
    columnTwo,
    enableH1,
  } = moduleData

  let { backgroundColor } = moduleData
  if (!backgroundColor) backgroundColor = 'white'

  const _getMarkdownRenderers = () => {
    const HeadingComponent = ({ children, level }: any) => {
      switch (level) {
        case 1:
          return (
            <div className={styles.heading}>
              <TitleSmall.Sans.Medium500>{children}</TitleSmall.Sans.Medium500>
            </div>
          )

        case 2:
        default:
          return (
            <div className={styles.subHeading}>
              <strong>{children}</strong>
            </div>
          )
      }
    }

    HeadingComponent.propTypes = {
      children: PropTypes.arrayOf(PropTypes.element.isRequired).isRequired,
      level: PropTypes.number.isRequired,
    }

    return {
      heading: HeadingComponent,
    }
  }

  const headingElement = enableH1 ? 'h1' : 'div'

  const getSingleColumn = () => (
    <>
      <div className={styles.flexRow}>
        <div className={styles.headingWrapper}>
          <div className={styles.heading}>
            <TitleLarge.Serif.Book500 element={headingElement}>
              <Markdown
                input={heading}
                allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
              />
            </TitleLarge.Serif.Book500>
          </div>
          {displaySubHeading && (
            <div className={styles.subHeading}>
              <Spacer.H16 />
              <TitleSmall.Sans.Light300>
                <Markdown
                  input={subHeading}
                  allowedMarkdownTypes={
                    MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY
                  }
                />
              </TitleSmall.Sans.Light300>
            </div>
          )}
        </div>
        <div className={styles.singleColumn}>
          <div className={styles.columnOne}>
            <Body.Regular400>
              <Markdown input={columnOne} renderers={_getMarkdownRenderers()} />
            </Body.Regular400>
          </div>
        </div>
      </div>
    </>
  )

  const getDoubleColumn = () => (
    <>
      <div className={styles.heading}>
        <TitleLarge.Serif.Book500 element={headingElement}>
          <Markdown
            input={heading}
            allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
          />
        </TitleLarge.Serif.Book500>
        {displaySubHeading && (
          <div className={styles.subHeading}>
            <Spacer.H16 />
            <TitleSmall.Sans.Light300>
              <Markdown
                input={subHeading}
                allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
              />
            </TitleSmall.Sans.Light300>
          </div>
        )}
      </div>
      <div className={styles.doubleColumn}>
        <div className={styles.columnOne}>
          <Body.Regular400>
            <Markdown
              className={styles.markdown}
              input={columnOne}
              renderers={_getMarkdownRenderers()}
            />
          </Body.Regular400>
        </div>
        <div className={styles.greyLine} />
        <div className={styles.columnTwo}>
          <Body.Regular400>
            <Markdown input={columnTwo} renderers={_getMarkdownRenderers()} />
          </Body.Regular400>
        </div>
      </div>
    </>
  )

  return (
    <ColoredPaddedContainer
      color={backgroundColor}
      className={styles.storyWrapper}
    >
      {columnTwo ? getDoubleColumn() : getSingleColumn()}
    </ColoredPaddedContainer>
  )
}

Story.propTypes = {
  moduleData: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    enableH1: PropTypes.bool,
    subHeading: PropTypes.string,
    displaySubHeading: PropTypes.bool.isRequired,
    columnOne: PropTypes.string.isRequired,
    columnTwo: PropTypes.string,
    backgroundColor: PropTypes.oneOf(['buttercup', 'moss', 'duckegg', 'white'])
      .isRequired,
    experimentKey: PropTypes.string,
  }),
}

export default Story
